import React, { useEffect, useState } from "react";
import {
  getPurchaseTally,
  receivePurchaseLine,
  closeShortPurchaseLine,
  deletePurchaseReceipt,
  addPurchaseInvoice,
  deletePurchaseInvoice,
  downloadPurchaseInvoice,
  downloadPurchaseOrderPdf,
} from "../../api/service";
import { fmtMoney, fmtQty } from "./shared";

const RUPEE = "\u20B9";
const PAGE_SIZE = 50;
const CHIPS = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "partial", label: "Partially received" },
  { id: "complete", label: "Fully received" },
];
const STATUS_BADGE = { open: "bg-secondary", partial: "bg-warning text-dark", complete: "bg-success" };
const STATUS_LABEL = { open: "Open", partial: "Partial", complete: "Complete" };
const TALLY = {
  "n/a": { label: "-", cls: "" },
  awaiting_invoice: { label: "Awaiting invoice", cls: "bg-info text-dark" },
  match: { label: "Match", cls: "bg-success" },
  mismatch: { label: "Mismatch", cls: "bg-danger" },
};
const LINE_STATE = {
  open: { label: "Not received", cls: "bg-secondary" },
  partial: { label: "Partial", cls: "bg-warning text-dark" },
  complete: { label: "Complete", cls: "bg-success" },
  short: { label: "Closed short", cls: "bg-dark" },
};

function TallyBadge({ value }) {
  const t = TALLY[value] || TALLY["n/a"];
  if (!t.cls) return <span className="text-muted">-</span>;
  return <span className={"badge " + t.cls}>{t.label}</span>;
}

function LineBlock({ line, money, canManage, colSpan, onDone, setErr, setMsg }) {
  const [qty, setQty] = useState("");
  const [on, setOn] = useState("");
  const [challan, setChallan] = useState("");
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isOpen = line.state === "open" || line.state === "partial";
  const remaining = Number(line.remaining_qty || 0);
  const st = LINE_STATE[line.state] || LINE_STATE.open;

  const add = async () => {
    setErr("");
    setMsg("");
    const q = parseFloat(qty);
    if (!(q > 0)) {
      setErr("Enter a received quantity above 0.");
      return;
    }
    if (q > remaining + 0.000001) {
      setErr("Over-receipt is not allowed: only " + fmtQty(remaining) + " " + line.unit + " remaining.");
      return;
    }
    setBusy(true);
    try {
      await receivePurchaseLine(line.id, { qty: qty, received_on: on, challan: challan });
      setMsg("Receipt recorded: " + fmtQty(q) + " " + line.unit + " - " + line.summary);
      setQty("");
      setOn("");
      setChallan("");
      onDone();
    } catch (e) {
      setErr(e.message || "Could not record the receipt.");
    } finally {
      setBusy(false);
    }
  };

  const closeShort = async () => {
    setErr("");
    setMsg("");
    if (reason.trim().length < 5) {
      setErr("Give a reason (at least 5 characters).");
      return;
    }
    if (!window.confirm("Close this line short at " + fmtQty(line.received_qty) + " " + line.unit + "? This cannot be undone.")) return;
    setBusy(true);
    try {
      await closeShortPurchaseLine(line.id, { reason: reason.trim() });
      setMsg("Line closed short: " + line.summary);
      setClosing(false);
      setReason("");
      onDone();
    } catch (e) {
      setErr(e.message || "Could not close the line.");
    } finally {
      setBusy(false);
    }
  };

  const delReceipt = async (rec) => {
    if (!window.confirm("Delete the receipt of " + fmtQty(rec.qty) + " " + line.unit + "?")) return;
    setErr("");
    setMsg("");
    try {
      await deletePurchaseReceipt(rec.id);
      onDone();
    } catch (e) {
      setErr(e.message || "Could not delete the receipt.");
    }
  };

  return (
    <React.Fragment>
      <tr>
        <td>
          {line.request_number}
          <div className="small text-muted">{line.project_label}</div>
        </td>
        <td>
          {line.summary}
          {line.exception && <span className="badge bg-warning text-dark ms-1">Exception</span>}
        </td>
        <td className="text-end">
          {fmtQty(line.approved_qty)} {line.unit}
        </td>
        <td className="text-end">
          {fmtQty(line.received_qty)} {line.unit}
        </td>
        <td className="text-end">
          {isOpen ? fmtQty(line.remaining_qty) + " " + line.unit : "-"}
        </td>
        <td>
          <span className={"badge " + st.cls}>{st.label}</span>
          {line.closed_short_reason && (
            <div className="small text-muted" title={line.closed_short_reason}>
              {line.closed_short_reason.length > 40 ? line.closed_short_reason.slice(0, 40) + "..." : line.closed_short_reason}
            </div>
          )}
        </td>
        {money && (
          <>
            <td className="text-end">{line.line_value !== undefined ? RUPEE + fmtMoney(line.line_value) : "-"}</td>
            <td className="text-end">{line.received_value !== undefined ? RUPEE + fmtMoney(line.received_value) : "-"}</td>
          </>
        )}
        <td style={{ minWidth: 330 }}>
          {isOpen && (
            <div className="d-flex gap-1 align-items-center flex-wrap">
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control form-control-sm"
                style={{ width: 80 }}
                placeholder="Qty"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 135 }}
                title="Received date (blank = today)"
                value={on}
                onChange={(e) => setOn(e.target.value)}
              />
              <input
                className="form-control form-control-sm"
                style={{ width: 100 }}
                maxLength={64}
                placeholder="Challan no."
                value={challan}
                onChange={(e) => setChallan(e.target.value)}
              />
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || !(parseFloat(qty) > 0)} onClick={add}>
                Add receipt
              </button>
              {Number(line.received_qty) > 0 && (
                <button type="button" className="btn btn-outline-dark btn-sm" disabled={busy} onClick={() => setClosing((c) => !c)}>
                  Close short
                </button>
              )}
            </div>
          )}
          {(line.receipts || []).map((rec) => (
            <div key={rec.id} className="small text-muted">
              {fmtQty(rec.qty)} {line.unit} on {String(rec.received_on).slice(0, 10)}
              {rec.challan ? " - " + rec.challan : ""}
              {rec.by ? " (" + rec.by + ")" : ""}
              {canManage && line.state !== "short" && (
                <button type="button" className="btn btn-link btn-sm text-danger py-0" onClick={() => delReceipt(rec)}>
                  delete
                </button>
              )}
            </div>
          ))}
        </td>
      </tr>
      {closing && (
        <tr>
          <td colSpan={colSpan}>
            <div className="p-2 bg-light border rounded d-flex gap-2 align-items-center">
              <span className="small">
                Complete this line at {fmtQty(line.received_qty)} of {fmtQty(line.approved_qty)} {line.unit}:
              </span>
              <input
                className="form-control form-control-sm"
                maxLength={500}
                placeholder="Reason for short supply (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <button type="button" className="btn btn-dark btn-sm" disabled={busy} onClick={closeShort}>
                Close short
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setClosing(false)}>
                Cancel
              </button>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function PoPanel({ po, canManage, onChanged }) {
  const money = po.tally !== undefined;
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [number, setNumber] = useState("");
  const [idate, setIdate] = useState("");
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const colSpan = 7 + (money ? 2 : 0);

  const addInvoice = async () => {
    setErr("");
    setMsg("");
    if (!number.trim()) {
      setErr("Enter the invoice number.");
      return;
    }
    if (!(parseFloat(amount) > 0)) {
      setErr("Enter the invoice amount (including GST).");
      return;
    }
    const fd = new FormData();
    fd.append("number", number.trim());
    if (idate) fd.append("invoice_date", idate);
    fd.append("amount", amount);
    if (file) fd.append("file", file);
    setBusy(true);
    try {
      await addPurchaseInvoice(po.id, fd);
      setMsg("Invoice " + number.trim() + " added.");
      setNumber("");
      setIdate("");
      setAmount("");
      setFile(null);
      setFileKey((k) => k + 1);
      onChanged();
    } catch (e) {
      setErr(e.message || "Could not add the invoice.");
    } finally {
      setBusy(false);
    }
  };

  const delInvoice = async (inv) => {
    if (!window.confirm("Delete invoice " + inv.number + "?")) return;
    setErr("");
    setMsg("");
    try {
      await deletePurchaseInvoice(inv.id);
      onChanged();
    } catch (e) {
      setErr(e.message || "Could not delete the invoice.");
    }
  };

  const download = async (inv) => {
    setErr("");
    try {
      await downloadPurchaseInvoice(inv.id, inv.original_name || "invoice-" + inv.number);
    } catch (e) {
      setErr(e.message || "Could not download the file.");
    }
  };

  const gap = Number(po.invoice_gap || 0);

  return (
    <div className="p-2 bg-light border rounded">
      {err && <div className="alert alert-danger py-1">{err}</div>}
      {msg && <div className="alert alert-success py-1">{msg}</div>}

      {money && (
        <div className="d-flex gap-4 flex-wrap align-items-center mb-2 small">
          <span>
            PO value: <b>{RUPEE}{fmtMoney(po.po_value)}</b>
          </span>
          <span>
            Received value: <b>{RUPEE}{fmtMoney(po.received_value)}</b>
          </span>
          <span>
            Invoiced (incl. GST): <b>{RUPEE}{fmtMoney(po.invoiced_total)}</b>
          </span>
          <span>
            Invoiced - received: <b className={Math.abs(gap) > 1 && po.status === "complete" ? "text-danger" : ""}>{gap > 0 ? "+" : ""}{RUPEE}{fmtMoney(po.invoice_gap)}</b>
          </span>
          <TallyBadge value={po.tally} />
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() =>
              downloadPurchaseOrderPdf(po.id, po.number).catch((e) => setErr(e.message || "Could not download the PO."))
            }
          >
            Download PO
          </button>
          {po.status !== "complete" && <span className="text-muted">Compared once every line is fully received or closed short.</span>}
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-sm align-middle mb-2" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Request</th>
              <th>Item</th>
              <th className="text-end">Approved</th>
              <th className="text-end">Received</th>
              <th className="text-end">Remaining</th>
              <th>State</th>
              {money && (
                <>
                  <th className="text-end">Line value</th>
                  <th className="text-end">Received value</th>
                </>
              )}
              <th>Record receipt</th>
            </tr>
          </thead>
          <tbody>
            {(po.lines || []).map((l) => (
              <LineBlock
                key={l.id}
                line={l}
                money={money}
                canManage={canManage}
                colSpan={colSpan}
                onDone={onChanged}
                setErr={setErr}
                setMsg={setMsg}
              />
            ))}
            {(po.lines || []).length === 0 && (
              <tr>
                <td colSpan={colSpan} className="text-muted">
                  No lines on this PO are visible to you.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {money && (
        <div>
          <div className="fw-bold small mb-1">Invoices</div>
          {(po.invoices || []).length === 0 && <div className="small text-muted mb-1">No invoices yet.</div>}
          {(po.invoices || []).map((inv) => (
            <div key={inv.id} className="small d-flex gap-3 align-items-center">
              <span>
                <b>{inv.number}</b> - {String(inv.invoice_date).slice(0, 10)}
              </span>
              <span>
                {RUPEE}
                {fmtMoney(inv.amount)}
              </span>
              {inv.by && <span className="text-muted">{inv.by}</span>}
              {inv.has_file ? (
                <button type="button" className="btn btn-link btn-sm py-0" onClick={() => download(inv)}>
                  download
                </button>
              ) : (
                <span className="text-muted">no file</span>
              )}
              {canManage && (
                <button type="button" className="btn btn-link btn-sm text-danger py-0" onClick={() => delInvoice(inv)}>
                  delete
                </button>
              )}
            </div>
          ))}
          {canManage && (
            <div className="d-flex gap-2 align-items-end flex-wrap mt-2">
              <div>
                <label className="form-label small mb-1">Invoice no.</label>
                <input className="form-control form-control-sm" style={{ width: 130 }} maxLength={64} value={number} onChange={(e) => setNumber(e.target.value)} />
              </div>
              <div>
                <label className="form-label small mb-1">Date (blank = today)</label>
                <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={idate} onChange={(e) => setIdate(e.target.value)} />
              </div>
              <div>
                <label className="form-label small mb-1">Amount incl. GST ({RUPEE})</label>
                <input type="number" min="0" step="0.01" className="form-control form-control-sm" style={{ width: 150 }} value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div>
                <label className="form-label small mb-1">File (PDF/JPG/PNG, optional)</label>
                <input
                  key={fileKey}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="form-control form-control-sm"
                  onChange={(e) => setFile((e.target.files && e.target.files[0]) || null)}
                />
              </div>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={addInvoice}>
                {busy ? "Saving..." : "Add invoice"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TallyTab({ opts }) {
  const canManage = !!(opts.can_price || opts.can_approve);
  const [chip, setChip] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ results: [], count: 0, status_counts: {}, mismatch_count: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const params = { page: page, page_size: PAGE_SIZE };
    if (chip === "open" || chip === "partial" || chip === "complete") params.status = chip;
    if (chip === "mismatch") params.tally = "mismatch";
    getPurchaseTally(params)
      .then((res) => {
        if (live) {
          setData(res);
          setErr("");
        }
      })
      .catch((e) => {
        if (live) setErr(e.message || "Could not load purchase orders.");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [chip, page, tick]);

  const rows = data.results || [];
  const money = rows.length > 0 && rows[0].tally !== undefined;
  const totalPages = Math.max(1, Math.ceil((data.count || 0) / PAGE_SIZE));
  const counts = data.status_counts || {};
  const colSpan = 5 + (money ? 4 : 0);
  const chips = canManage ? CHIPS.concat([{ id: "mismatch", label: "Invoice mismatch" }]) : CHIPS;

  return (
    <div>
      <div className="d-flex gap-2 flex-wrap mb-3">
        {chips.map((c) => {
          const n = c.id === "all" ? null : c.id === "mismatch" ? data.mismatch_count : counts[c.id];
          return (
            <button
              key={c.id}
              type="button"
              className={"btn btn-sm " + (chip === c.id ? "btn-primary" : "btn-outline-primary")}
              onClick={() => {
                setChip(c.id);
                setPage(1);
                setOpenId(null);
              }}
            >
              {c.label}
              {n !== null && n !== undefined ? " (" + n + ")" : ""}
            </button>
          );
        })}
      </div>

      {err && <div className="alert alert-danger py-2">{err}</div>}

      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>PO</th>
              <th>Vendor</th>
              <th>Lines</th>
              <th>Status</th>
              <th>Created</th>
              {money && (
                <>
                  <th className="text-end">PO value</th>
                  <th className="text-end">Received value</th>
                  <th className="text-end">Invoiced</th>
                  <th>Invoice tally</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((po) => (
              <React.Fragment key={po.id}>
                <tr style={{ cursor: "pointer" }} onClick={() => setOpenId(openId === po.id ? null : po.id)}>
                  <td>
                    <b>{po.number}</b>
                  </td>
                  <td>
                    {po.vendor.name}
                    {po.vendor.is_one_time ? " (one-time)" : ""}
                  </td>
                  <td>{po.line_count}</td>
                  <td>
                    <span className={"badge " + (STATUS_BADGE[po.status] || "bg-secondary")}>{STATUS_LABEL[po.status] || po.status}</span>
                  </td>
                  <td>{String(po.created_at).slice(0, 10)}</td>
                  {money && (
                    <>
                      <td className="text-end">{RUPEE}{fmtMoney(po.po_value)}</td>
                      <td className="text-end">{RUPEE}{fmtMoney(po.received_value)}</td>
                      <td className="text-end">{RUPEE}{fmtMoney(po.invoiced_total)}</td>
                      <td>
                        <TallyBadge value={po.tally} />
                      </td>
                    </>
                  )}
                </tr>
                {openId === po.id && (
                  <tr>
                    <td colSpan={colSpan}>
                      <PoPanel po={po} canManage={canManage} onChanged={() => setTick((t) => t + 1)} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="text-center text-muted py-3">
                  No purchase orders here. A PO is created when Amit approves priced lines.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={colSpan} className="text-center text-muted py-3">
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex align-items-center justify-content-between">
        <span className="small text-muted">{data.count || 0} PO(s)</span>
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span className="small">
            Page {page} of {totalPages}
          </span>
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
